import React from 'react';
import {
  FiAlertTriangle,
  FiCheckCircle,
  FiInfo,
  FiTarget,
} from 'react-icons/fi';
import type {
  ChannelFunnel,
  ChannelFunnelDiagnosis,
} from '@troia-v3/types';

import { LeadSourceIcon } from '@/shared/icons/LeadSourceIcon';
import { DashboardSectionCard } from './DashboardSectionCard';

interface ChannelFunnelGridProps {
  channels: ChannelFunnel[];
  /** `true` quando spend/cpl/roas vêm zerados — esconde linhas de spend. */
  noAdSpendData?: boolean;
}

/**
 * Decisão B refinado (rodada visual 2026-05-01): todos os cards usam roxo
 * TROIA `#8b5cf6` uniforme — diferenciação por ícone (LeadSourceIcon) e
 * label, não por cor.
 */
const TROIA_PURPLE_DARK = '#7c3aed';
const TROIA_PURPLE_BG_SOFT = 'rgba(139, 92, 246, 0.12)';

const formatPct = (v: number): string =>
  `${new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 }).format(v)}%`;

const formatNumber = (v: number): string =>
  new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 }).format(v);

/**
 * Cores semânticas do diagnóstico (fidelidade ao mockup): verde `success` pra
 * `good`, âmbar `warning` pra `warning`, vermelho `error` pra `critical`.
 */
const SEVERITY_COLOR = {
  good: '#10b981',
  warning: '#f59e0b',
  critical: '#ef4444',
} as const;

const SeverityIcon: React.FC<{ severity: ChannelFunnelDiagnosis['severity'] }> = ({ severity }) => {
  if (severity === 'good') return <FiCheckCircle size={12} />;
  if (severity === 'warning') return <FiInfo size={12} />;
  return <FiAlertTriangle size={12} />;
};

/** Barra de funil (intensidade crescente do roxo por etapa). */
interface FunnelBarProps {
  label: string;
  value: number;
  pct: number;
  color: string;
}

const FunnelBar: React.FC<FunnelBarProps> = ({ label, value, pct, color }) => (
  <div className="grid items-center gap-2 text-xs" style={{ gridTemplateColumns: '72px 1fr auto' }}>
    <span className="truncate" style={{ color: 'var(--theme-muted-text)' }}>
      {label}
    </span>
    <div
      className="h-1.5 rounded-full overflow-hidden"
      style={{ backgroundColor: 'var(--theme-page-background)' }}
    >
      <div
        className="h-full rounded-full transition-all"
        style={{ width: `${Math.max(3, Math.min(100, pct))}%`, backgroundColor: color }}
      />
    </div>
    <span
      className="text-right font-semibold"
      style={{ color: 'var(--theme-card-text)', fontVariantNumeric: 'tabular-nums' }}
    >
      {formatNumber(value)}
    </span>
  </div>
);

interface KpiCellProps {
  label: string;
  value: string;
  highlight?: boolean;
}

const KpiCell: React.FC<KpiCellProps> = ({ label, value, highlight }) => (
  <div
    className="rounded-lg px-2.5 py-2"
    style={{
      backgroundColor: highlight ? 'rgba(139, 92, 246, 0.08)' : 'var(--theme-page-background)',
    }}
  >
    <div className="text-[11px]" style={{ color: 'var(--theme-muted-text)' }}>
      {label}
    </div>
    <div
      className="text-sm font-bold mt-0.5"
      style={{ color: 'var(--theme-card-text)', letterSpacing: '-0.02em' }}
    >
      {value}
    </div>
  </div>
);

interface ChannelCardProps {
  channel: ChannelFunnel;
  noAdSpendData: boolean;
}

const ChannelCard: React.FC<ChannelCardProps> = ({ channel, noAdSpendData }) => {
  const totalVisitors = Math.max(1, channel.funnel.visitors);
  const totalLeads = channel.funnel.leads;
  const leadsPct = 100 * (channel.funnel.leads / totalVisitors);
  const proposalsPct = totalLeads === 0 ? 0 : (channel.funnel.proposals / totalLeads) * 100;
  const wonPct = totalLeads === 0 ? 0 : (channel.funnel.won / totalLeads) * 100;

  return (
    <div
      className="rounded-lg p-3.5 flex flex-col gap-3"
      style={{
        backgroundColor: 'var(--theme-card-background)',
        border: '1px solid var(--theme-card-border)',
      }}
      data-testid={`channel-funnel-card-${channel.source}`}
    >
      <div className="flex items-center gap-2.5">
        <span
          className="flex items-center justify-center flex-shrink-0"
          style={{ width: 34, height: 34, borderRadius: 9, backgroundColor: TROIA_PURPLE_BG_SOFT }}
        >
          <LeadSourceIcon source={channel.source} size={17} color={TROIA_PURPLE_DARK} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold truncate" style={{ color: 'var(--theme-card-text)' }}>
            {channel.label}
          </div>
          <div className="text-xs mt-0.5" style={{ color: 'var(--theme-muted-text)' }}>
            {channel.activeCampaigns} {channel.activeCampaigns === 1 ? 'campanha' : 'campanhas'} · {formatPct(channel.leadsSharePct)} dos leads
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        {!noAdSpendData && (
          <>
            <KpiCell label="Spend" value={`R$ ${formatNumber(channel.spend)}`} />
            <KpiCell label="CPL" value={`R$ ${formatNumber(channel.cpl)}`} />
          </>
        )}
        <KpiCell
          label="Receita"
          value={`R$ ${formatNumber(channel.revenue)}`}
          highlight={noAdSpendData}
        />
        {!noAdSpendData ? (
          <KpiCell label="ROAS" value={`${channel.roas.toFixed(1)}x`} />
        ) : (
          <KpiCell label="Leads" value={formatNumber(channel.funnel.leads)} highlight />
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        <FunnelBar label="Visitantes" value={channel.funnel.visitors} pct={100} color="rgba(139, 92, 246, 0.45)" />
        <FunnelBar label="Leads" value={channel.funnel.leads} pct={leadsPct} color="rgba(139, 92, 246, 0.6)" />
        <FunnelBar label="Propostas" value={channel.funnel.proposals} pct={proposalsPct} color="rgba(139, 92, 246, 0.78)" />
        <FunnelBar label="Ganhos" value={channel.funnel.won} pct={wonPct} color="rgba(139, 92, 246, 0.95)" />
      </div>

      <div
        className="flex items-center gap-1.5 text-xs font-semibold pt-2.5 mt-auto"
        style={{
          borderTop: '1px solid var(--theme-card-border)',
          color: SEVERITY_COLOR[channel.diagnosis.severity],
        }}
      >
        <span
          className="flex-shrink-0"
          style={{ color: SEVERITY_COLOR[channel.diagnosis.severity] }}
        >
          <SeverityIcon severity={channel.diagnosis.severity} />
        </span>
        <span>{channel.diagnosis.message}</span>
      </div>
    </div>
  );
};

const NoAdSpendBadge: React.FC = () => (
  <span
    className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full"
    style={{ backgroundColor: 'var(--theme-page-background)', color: 'var(--theme-muted-text)' }}
    title="Sem integração com plataformas de ads — Spend, CPL e ROAS estão zerados."
  >
    <FiInfo size={11} />
    sem dados de ad spend
  </span>
);

export const ChannelFunnelGrid: React.FC<ChannelFunnelGridProps> = ({
  channels,
  noAdSpendData = false,
}) => {
  if (channels.length === 0) {
    return (
      <DashboardSectionCard
        icon={<FiTarget size={16} style={{ color: 'var(--theme-card-text)' }} />}
        title="Funil por canal"
        subtitle="Desempenho por origem de tráfego"
        badge={noAdSpendData ? <NoAdSpendBadge /> : undefined}
        testId="channel-funnel-grid"
      >
        <div
          className="rounded-lg p-8 text-center text-sm"
          style={{
            border: '1px dashed var(--theme-card-border)',
            color: 'var(--theme-muted-text)',
          }}
          data-testid="channel-funnel-grid-empty"
        >
          Sem leads atribuídos a canais no período.
        </div>
      </DashboardSectionCard>
    );
  }

  return (
    <DashboardSectionCard
      icon={<FiTarget size={16} style={{ color: 'var(--theme-card-text)' }} />}
      title="Funil por canal"
      subtitle="Desempenho por origem de tráfego"
      badge={noAdSpendData ? <NoAdSpendBadge /> : undefined}
      testId="channel-funnel-grid"
    >
      <div className="grid grid-cols-1 min-[560px]:grid-cols-2 min-[1180px]:grid-cols-4 gap-3">
        {channels.map((channel) => (
          <ChannelCard key={channel.source} channel={channel} noAdSpendData={noAdSpendData} />
        ))}
      </div>
    </DashboardSectionCard>
  );
};

ChannelFunnelGrid.displayName = 'ChannelFunnelGrid';
